import { ArrowUpRight, Heart, Plus } from "lucide-react";
import { Link } from "react-router";
import type { CommunityNote, CommunityTopic } from "../../../api/community";
import { MediaImage } from "../../../components/MediaImage";
import { Skeleton } from "../../../components/Skeleton";
import { authorInitial, formatCount, thumbForNote } from "../../community/community-data";

type HomeCommunityProps = {
  notes: CommunityNote[];
  topics: CommunityTopic[];
  loading: boolean;
};

function CommunityTile({ note, lead }: { note: CommunityNote; lead: boolean }) {
  const cover = thumbForNote(note);
  return (
    <Link className={`home-tile${lead ? " is-lead" : ""}`} to={`/community/${note.id}`} aria-label={`查看作品：${note.title}`}>
      <span className="home-tile-media">{cover ? <MediaImage src={cover} alt={note.title} /> : null}</span>
      <span className="home-tile-copy">
        <strong>{note.title}</strong>
        <span className="home-tile-meta">
          <em>{authorInitial(note.authorName)}</em>
          {note.authorName || "匿名设计师"}
          <span><Heart size={12} /> {formatCount(note.likeCount || 0)}</span>
        </span>
      </span>
      <i aria-hidden="true"><ArrowUpRight size={16} strokeWidth={1.8} /></i>
    </Link>
  );
}

/** 主卡给点赞最多的作品，其余保持原来的发布顺序。 */
function orderTiles(notes: CommunityNote[]) {
  if (notes.length === 0) return notes;
  const lead = notes.reduce((best, note) => ((note.likeCount || 0) > (best.likeCount || 0) ? note : best), notes[0]);
  return [lead, ...notes.filter((note) => note.id !== lead.id)];
}

/** 社区拼贴：一张主作品 + 三张作品 + 一张“发布你的作品”入口，突出社区参与。 */
export function HomeCommunity({ notes, topics, loading }: HomeCommunityProps) {
  const tiles = orderTiles(notes.slice(0, 4));

  return (
    <section className="home-community" aria-labelledby="home-community-title" aria-busy={loading}>
      <header className="home-community-head">
        <div>
          <h2 id="home-community-title">社区正在发生</h2>
          <p>设计师每天在这里发布情绪板与材质实践，点开任意一件都能看到它用了什么。</p>
        </div>
        {topics.length > 0 ? (
          <nav className="home-community-topics" aria-label="热门话题">
            {topics.slice(0, 6).map((topic) => (
              <Link key={topic.id} to={`/community?topic=${encodeURIComponent(topic.name)}`}>#{topic.name}</Link>
            ))}
          </nav>
        ) : null}
      </header>
      {loading ? (
        <div className="home-community-bento" aria-hidden="true">
          {Array.from({ length: 5 }, (_, index) => (
            <span className={`home-tile${index === 0 ? " is-lead" : ""}`} key={index}><Skeleton className="is-fill" /></span>
          ))}
        </div>
      ) : tiles.length > 0 ? (
        <div className="home-community-bento">
          {tiles.map((note, index) => <CommunityTile note={note} lead={index === 0} key={note.id} />)}
          <Link className="home-tile is-publish" to="/community">
            <i aria-hidden="true"><Plus size={22} strokeWidth={1.8} /></i>
            <strong>发布你的作品</strong>
            <span>把你的情绪板放进社区，让更多设计师看到。</span>
          </Link>
        </div>
      ) : (
        <div className="home-status">
          <p>还没有新作品。发布你的第一张情绪板，它会出现在这里。</p>
          <Link to="/community">去社区发布</Link>
        </div>
      )}
    </section>
  );
}
